/**
 * What the checkout should do about the `paymentStatus` the backend sent back.
 *
 * ## Why this is not a switch over `PaymentStatus` in the page
 *
 * It was, and the switch had no `default`. `PaymentStatus` in `models/APIModels.ts` lists
 * six values, and TypeScript is happy to treat a switch covering all six as exhaustive —
 * but the value arrives as a **string off the wire**, and both checkout endpoints hand
 * back Stripe's own status verbatim:
 *
 *   `OrderRepository` (`/order/create`)          `paymentStatus = paymentIntent.status`
 *   `RemovalRepository` (`/removals/order/create`) `paymentStatus = paymentIntent.status`
 *
 * Stripe's set is larger than ours — `processing`, `requires_confirmation`,
 * `requires_capture`, `canceled` — and `processing` is not exotic: it is what an async
 * method such as Klarna or BACS returns, and a card can land there too. When one of those
 * came back the switch matched nothing, `handlePaymentStatus` returned, and **the customer
 * pressed Pay, an order was created, and the page did nothing at all**. On the wallet path
 * there is no `finally`, so the button went on spinning.
 *
 * Mapping the wire value to a set we own, in one place, is what makes the switch in the
 * page genuinely exhaustive: `PaymentOutcome` is ours, so leaving a branch out is a
 * compile error rather than silence. The argument is typed `string` on purpose — that is
 * what it is.
 */
export type PaymentOutcome =
  /** Paid, free, or a duplicate the backend recognised. Go to the success page. */
  | 'order-complete'
  /** 3DS or similar. Hand back to Stripe to challenge the customer. */
  | 'needs-authentication'
  /** Declined. Say so and let them try another card. */
  | 'needs-another-method'
  /**
   * The order exists and the payment has not settled — or the status is one this build
   * does not know. Never silent: say what happened and put the button back.
   */
  | 'unconfirmed';

export function outcomeForPaymentStatus(status: string | null | undefined): PaymentOutcome {
  switch (status) {
    case 'succeeded':
    case 'none_needed':
    case 'trialing':
    case 'duplicate_prevented':
      return 'order-complete';
    case 'requires_action':
      return 'needs-authentication';
    case 'requires_payment_method':
      return 'needs-another-method';
    default:
      return 'unconfirmed';
  }
}
